import { useMemo, useState } from "react";
import { Plus, Loader2, Save, FileUp, Search, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const emptyHolder = () => ({ kind: "person", name: "", id_number: "", email: "", contact_name: "", contact_email: "", verified: false });
const EMPTY = { shareholder_id: "", shares: "", share_class: "", note: "", date: "" };

export function NewDepotDialog({ companyId, rows = [], classes = [], onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [holders, setHolders] = useState([emptyHolder()]);
  const [file, setFile] = useState(null);
  const [proffIdx, setProffIdx] = useState(-1);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const classNames = classes.map((c) => c.name).filter(Boolean);
  const selected = useMemo(() => rows.find((r) => r.id === form.shareholder_id) || null, [rows, form.shareholder_id]);

  const reset = () => { setForm(EMPTY); setHolders([emptyHolder()]); setFile(null); setSaving(false); setProffIdx(-1); };
  const onOpenChange = (o) => { if (o) reset(); setOpen(o); };

  const updH = (i, patch) => setHolders((list) => list.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const addH = () => setHolders((list) => [...list, emptyHolder()]);
  const removeH = (i) => setHolders((list) => (list.length > 1 ? list.filter((_, idx) => idx !== i) : list));

  const fetchProffName = async (i, org) => {
    const d = (org || "").replace(/\D/g, "");
    if (d.length !== 9) return toast.error("Skriv inn org.nr (9 siffer) først");
    setProffIdx(i);
    try {
      const { data } = await api.get(`/proff/company/${d}`);
      if (data?.name) updH(i, { name: data.name, verified: true });
    } catch {
      // stille – navn kan skrives manuelt
    } finally {
      setProffIdx(-1);
    }
  };

  const save = async () => {
    if (!form.shareholder_id) return toast.error("Velg aksjonæren som legger aksjer i depo");
    const n = Number(form.shares);
    if (!Number.isFinite(n) || n <= 0) return toast.error("Antall aksjer må være større enn 0");
    const clean = holders.filter((h) => h.name.trim());
    if (clean.length === 0) return toast.error("Legg til minst én depomottaker med navn");
    for (const h of clean) {
      if (h.kind === "selskap" && (!h.contact_name.trim() || !h.contact_email.trim()))
        return toast.error(`Depomottaker-selskap «${h.name}» må ha en kontaktperson med navn og e-post`);
    }

    const payload = clean.map((h) => ({
      kind: h.kind, name: h.name.trim(), id_number: h.id_number || "",
      email: h.kind === "person" ? (h.email || "").trim() : "",
      contact_name: h.kind === "selskap" ? h.contact_name.trim() : "",
      contact_email: h.kind === "selskap" ? h.contact_email.trim() : "",
    }));

    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("shareholder_id", form.shareholder_id);
    fd.append("shares", String(Math.round(n)));
    fd.append("share_class", form.share_class || "");
    fd.append("holders", JSON.stringify(payload));
    fd.append("note", form.note || "");
    fd.append("date", form.date || "");
    setSaving(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/depots`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data?.notified ? `Depo registrert – varsel sendt til ${data.notified} mottaker(e)` : "Depo registrert i aksjeeierboka");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5" data-testid="new-depot-btn">
          <Plus className="h-4 w-4" /> Registrer depo
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="new-depot-dialog">
        <DialogHeader>
          <DialogTitle>Registrer depo på aksjer</DialogTitle>
          <DialogDescription>
            Registrer depo på en aksjonærs aksjer, hvem som er depomottaker (én eller flere), og legg gjerne ved avtale og kommentar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <Label className="text-xs">Aksjonær</Label>
            <Select value={form.shareholder_id} onValueChange={(v) => { set("shareholder_id", v); set("share_class", ""); }}>
              <SelectTrigger className="mt-1.5" data-testid="depot-shareholder-select"><SelectValue placeholder="Velg aksjonær" /></SelectTrigger>
              <SelectContent>
                {rows.map((r) => (
                  <SelectItem key={r.id} value={r.id} data-testid={`depot-shareholder-${r.id}`}>{r.name} · {r.shares} aksjer</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Antall aksjer i depo</Label>
              <Input type="number" inputMode="numeric" className="mt-1.5" data-testid="depot-shares-input" value={form.shares} onChange={(e) => set("shares", e.target.value)} placeholder="0" />
            </div>
            <div>
              <Label className="text-xs">Aksjeklasse</Label>
              {classNames.length ? (
                <Select value={form.share_class || "__all__"} onValueChange={(v) => set("share_class", v === "__all__" ? "" : v)}>
                  <SelectTrigger className="mt-1.5" data-testid="depot-class-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Alle klasser</SelectItem>
                    {classNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input className="mt-1.5" value={form.share_class} onChange={(e) => set("share_class", e.target.value)} placeholder="Alle klasser" />
              )}
            </div>
          </div>
          {selected ? (
            <p className="-mt-2 text-[11px] text-muted-foreground" data-testid="depot-available-hint">
              {selected.name} eier {selected.shares} aksjer totalt.
            </p>
          ) : null}

          <div className="space-y-3">
            <Label className="text-xs">Depomottakere</Label>
            {holders.map((h, i) => {
              const isCompany = h.kind === "selskap";
              return (
                <div key={i} className="rounded-lg border border-border/60 bg-muted/20 p-3" data-testid={`holder-block-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Depomottaker {i + 1}</span>
                    {holders.length > 1 ? (
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" data-testid={`holder-remove-${i}`} onClick={() => removeH(i)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                  </div>

                  <div className="mt-2 flex gap-1.5 rounded-lg border border-border p-1">
                    {[["person", "Person"], ["selskap", "Selskap"]].map(([v, l]) => (
                      <button
                        key={v}
                        type="button"
                        data-testid={`holder-kind-${i}-${v}`}
                        onClick={() => updH(i, { kind: v, id_number: "", verified: false })}
                        className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${h.kind === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>

                  {isCompany ? (
                    <div className="mt-2">
                      <Label className="text-xs">Org.nr (valgfritt – henter firmanavn)</Label>
                      <Input className="mt-1.5" inputMode="numeric" data-testid={`holder-org-${i}`} value={h.id_number} onChange={(e) => updH(i, { id_number: e.target.value, verified: false })} placeholder="9 siffer" />
                    </div>
                  ) : null}

                  <div className="mt-2">
                    <Label className="text-xs">Navn på depomottaker</Label>
                    <div className="mt-1.5 flex gap-2">
                      <Input data-testid={`holder-name-${i}`} value={h.name} onChange={(e) => updH(i, { name: e.target.value, verified: false })} placeholder={isCompany ? "Firmanavn" : "Navn"} />
                      {isCompany ? (
                        <Button type="button" variant="outline" className="shrink-0 gap-1.5" data-testid={`holder-proff-${i}`} disabled={proffIdx === i} onClick={() => fetchProffName(i, h.id_number)}>
                          {proffIdx === i ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Hent
                        </Button>
                      ) : null}
                    </div>
                    {h.verified ? (
                      <p className="mt-1 flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400" data-testid={`holder-verified-${i}`}><Check className="h-3.5 w-3.5" /> Bekreftet mot Proff</p>
                    ) : null}
                  </div>

                  {isCompany ? (
                    <div className="mt-2 space-y-2">
                      <Label className="text-xs font-medium">Kontaktperson for selskapet (påkrevd)</Label>
                      <Input data-testid={`holder-contact-name-${i}`} value={h.contact_name} onChange={(e) => updH(i, { contact_name: e.target.value })} placeholder="Navn på kontaktperson" />
                      <Input type="email" data-testid={`holder-contact-email-${i}`} value={h.contact_email} onChange={(e) => updH(i, { contact_email: e.target.value })} placeholder="E-post" />
                    </div>
                  ) : (
                    <div className="mt-2">
                      <Label className="text-xs">E-post til depomottaker (valgfritt)</Label>
                      <Input type="email" className="mt-1.5" data-testid={`holder-email-${i}`} value={h.email} onChange={(e) => updH(i, { email: e.target.value })} placeholder="For varsel med lenke til avtalen" />
                    </div>
                  )}
                </div>
              );
            })}
            <Button type="button" variant="outline" size="sm" className="w-full gap-1.5" data-testid="add-holder-btn" onClick={addH}>
              <Plus className="h-4 w-4" /> Legg til depomottaker
            </Button>
          </div>

          <div>
            <Label className="text-xs">Depoavtale (valgfritt)</Label>
            <label className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-3 text-sm hover:bg-muted/40" data-testid="depot-file-label">
              <FileUp className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate text-muted-foreground">{file ? file.name : "Velg avtalefil (PDF) …"}</span>
              <input type="file" accept="application/pdf,.pdf,image/*" className="hidden" data-testid="depot-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Dato (valgfritt)</Label>
              <Input type="date" className="mt-1.5" data-testid="depot-date-input" value={form.date} onChange={(e) => set("date", e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Kommentar (valgfritt)</Label>
            <Textarea className="mt-1.5" rows={2} data-testid="depot-note-input" value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="F.eks. type depo, depotkonto eller annen merknad" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Avbryt</Button>
          <Button className="gap-2" data-testid="depot-save-btn" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Registrer depo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
